import { FilteredSelect as FieldSelector } from './FieldSelector';
import { ElementWithFeedbackContainer } from './ElementWithFeedbackContainer';
import { arrayUtils, generateId, modalUtils } from '@/app/core/utils';
import { uploadInputFile } from '@/app/features/products/common/components/frontend-processing/services';
import { parseErrorSchema } from '@/app/core/helpers/yup-resolver-helpers';
import {
  FieldError,
  FieldHeadersMappingsMap,
  FieldMappings,
  PreviousFieldMappings,
  StringToBooleanMap,
  validFieldsConsumerAggregateErrors,
} from '@/app/features/products/common/components/frontend-processing/models';
import {
  calculateTableColumnWidths,
  FileContentValidationResult,
  TableColumnWidths,
  validateFileContent,
  generateInitialFieldMappings,
  toFieldMappingsMapFromMap,
  updateFieldMappings,
} from '@/app/features/products/common/components/frontend-processing/utils';
import {
  fieldHeadersValidator,
  InputFileDetails,
  InputFileSelectionOptions,
  InputFileSelector,
} from '@/app/features/products/common/components/file-selection';
import { saveLayout } from '@/app/features/products/common/services';
import { useDataStore } from '@/app/core/components/step-wizard';
import { Table } from 'antd';
import { ColumnType } from 'antd/lib/table';
import { useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import { transformToNestObject } from 'react-hook-form';
import {
  InputLabelsMap,
  HeaderInfo,
  FileLayout,
  isLayoutRequired,
  addonProductsSchemaValdiator,
  SSARProgram,
} from '@ssar-app/domain';
import lodashIsArray from 'lodash/isArray';
import lodashToPairs from 'lodash/toPairs';
import { FaCheck, FaCheckDouble } from 'react-icons/fa/index.js';
import { Alert, Popover, Spin } from '@ssar-app/uikit';
import { Button, HStack, Icon, Text, Tooltip } from '@chakra-ui/react';
import { AiOutlineUpload } from 'react-icons/ai/index.js';
import { inputFieldsCombinationsValidator } from '@ssar-app/domain/src/models/program/products/ascend-quest/validators/input-layout-validator';

export interface InputFieldMapperProps {
  productName: string;
  inputFileDetails: InputFileDetails;
  tmsNumber: string;
  subCode: string;
  inputLabelsMap: InputLabelsMap;
  previousFieldMappings: PreviousFieldMappings[];
  onFieldErrorsOrWarnings: (
    errors: string[],
    fieldErrors: FieldError[],
    isSSNMapped: boolean,
  ) => void;
  onMapperDisplayed?: () => void;
  onSuccess: (
    clientFileName: string,
    serverFileName: string,
    fieldMappings: FieldMappings[],
    hasHeaders?: boolean,
    recordsExpected?: number,
  ) => void;
  onNewFileSelected: (
    details: InputFileDetails,
    currentFieldMappings: FieldMappings[],
  ) => void;
  isRemoteFile: boolean;
  setIsRemoteFile: (arg0: boolean) => void;
  inputFileSelectionOptions: InputFileSelectionOptions;
  oneClickRemoteFileAccess?: boolean;
}

const RowSamplingCount = 100;

interface InputFieldMapperViewModel {
  headersSelectedStatusMap: StringToBooleanMap[];
  fieldErrors: FieldError[];
  fieldHeaderMappingsMap: FieldHeadersMappingsMap[];
  aggregateErrors: string[];
  isSSNMapped: boolean;
}

const generateViewModel = (
  headerInfo: HeaderInfo,
  fieldMappings: PreviousFieldMappings[],
  inputLabelsMap: InputLabelsMap,
): InputFieldMapperViewModel => {
  // console.log('Generating View Model', headerInfo, fieldMappings);
  const { fieldHeaderMappingsMap, headerSelectionStatuses } =
    generateInitialFieldMappings(
      headerInfo,
      toFieldMappingsMapFromMap(fieldMappings),
      inputLabelsMap,
    );
  const initialModel: InputFieldMapperViewModel = {
    headersSelectedStatusMap: headerSelectionStatuses,
    fieldErrors: [],
    aggregateErrors: [],
    isSSNMapped: false,
    fieldHeaderMappingsMap,
  };
  return initialModel;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleFileUploadError = (e: any): void => {
  modalUtils.showError(e);
};
export const InputFieldMapper = (props: InputFieldMapperProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { data } = useDataStore();

  const {
    inputFileDetails,
    previousFieldMappings,
    onSuccess,
    inputLabelsMap,
    productName,
    inputFileSelectionOptions,
  } = props;

  const toCalculateColumnWidths = useMemo(
    () =>
      inputFileDetails.headersInfo.actualHeaders[0] !== ''
        ? inputFileDetails.headersInfo.actualHeaders
        : inputFileDetails.headersInfo.detectedHeaders.map((e) => e[0]),
    [inputFileDetails.headersInfo],
  );

  const columnsRange = arrayUtils.createRange(
    inputFileDetails.scannedMaxColumnCount,
  );
  const [isContentValidationStale, setIsContentValidationStale] =
    useState(true);
  // const [isRemoteFile, setIsRemoteFile] = useState<boolean>(props.isRemoteFile);

  const [fileContentValidationResults, setFileContentValidationResults] =
    useState<FileContentValidationResult[]>([]);

  const maxRowsInData = inputFileDetails.data.reduce((acc, curr) => {
    return acc < curr.length ? curr.length : acc;
  }, 0);
  const effectiveRowSamplingCount = Math.min(maxRowsInData, RowSamplingCount);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rows: any[] = arrayUtils
    .createRange(effectiveRowSamplingCount)
    .map(() => {
      return columnsRange.concat([]);
    });

  // console.log('previousFieldMappings: ', previousFieldMappings);

  const [viewModel, setViewModel] = useState<InputFieldMapperViewModel>(() =>
    generateViewModel(
      inputFileDetails.headersInfo,
      previousFieldMappings,
      inputLabelsMap,
    ),
  );

  const {
    headersSelectedStatusMap,
    fieldErrors,
    aggregateErrors,
    fieldHeaderMappingsMap,
  } = viewModel;

  // console.log('fieldHeaderMappingsMap: ', fieldHeaderMappingsMap);
  const effectiveHeaders = fieldHeaderMappingsMap.map(
    (a) => a.fieldHeaderMapping.experianHeader || '',
  );

  const fieldHeadersData = fieldHeaderMappingsMap.map(
    (a) => a.fieldHeaderMapping,
  );

  const [validatedHeaderMappings, setValidatedHeaderMappings] =
    useState(fieldHeadersData);

  const tableHeightInPixels = 500;
  const [widthsDetails, setWidthDetails] = useState<TableColumnWidths>({
    columnWidths: [],
    tableWidthInPixels: 0,
  });

  const stringifiedSelectedHeaders = JSON.stringify(
    fieldHeadersData.map((a) => a.experianHeader),
  );

  useEffect(() => {
    // console.time('Validation');
    const isSSNMapped = effectiveHeaders
      .map((a) => a?.toLowerCase())
      .includes('ssn');
    let newModel = viewModel;

    fieldHeadersValidator
      .validate(effectiveHeaders)
      .then(() => {
        newModel = {
          ...newModel,
          fieldErrors: [],
          aggregateErrors: [],
          isSSNMapped,
        };
      })
      .catch((err) => {
        const result = parseErrorSchema(err, true);
        const xfmedResult = transformToNestObject(result);
        const errors: FieldError[] = lodashIsArray(xfmedResult)
          ? xfmedResult
          : lodashToPairs(xfmedResult).reduce((acc, curr) => {
              const index = +curr[0];
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const value = curr[1] as any;
              const xfmedValues: FieldError = {
                message: value?.message,
                type: value?.type,
              };
              acc[index] = xfmedValues;
              return acc;
            }, [] as FieldError[]);
        // console.log('result: ', errors);
        newModel = {
          ...newModel,
          fieldErrors: errors,
          isSSNMapped,
        };
      })
      .then(async () => {
        try {
          return inputFieldsCombinationsValidator.validate(effectiveHeaders, {
            abortEarly: false,
          });
        } catch (err) {
          console.error(err);
          throw err;
        }
      })
      .catch((err) => {
        if (yup.ValidationError.isError(err) && err.inner && err.inner.length) {
          const errorTypes = err.inner
            .map((a) => a.type)
            .filter((a) => {
              return validFieldsConsumerAggregateErrors.includes(a);
            });
          newModel = {
            ...newModel,
            aggregateErrors: errorTypes,
            isSSNMapped,
          };
        } else {
          console.error('Received unknown error during validation', err);
        }
      })
      .then(async () => {
        return addonProductsSchemaValdiator.validate(
          {
            addOnProducts: (data as SSARProgram)?.scoresAndAddonProducts
              ?.addonProducts,
            mappedHeaders: effectiveHeaders,
          },
          {
            abortEarly: false,
          },
        );
      })
      .catch((err) => {
        if (err?.errors.length > 0) {
          newModel.fieldErrors.push({ message: err.errors[0], type: 'Error' });
        }
      })
      .finally(() => {
        setViewModel(newModel);
        // console.timeEnd('Validation');
        props.onFieldErrorsOrWarnings(
          newModel.aggregateErrors,
          newModel.fieldErrors,
          isSSNMapped,
        );
        setIsValidating(false);
      });

    setIsContentValidationStale(
      // stringifiedExperianHeaders !== JSON.stringify(validatedHeaders),
      fieldHeaderMappingsMap.some(
        (a, index) =>
          a.fieldHeaderMapping.experianHeader?.toLowerCase() !==
          validatedHeaderMappings[index]?.experianHeader?.toLowerCase(),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(stringifiedSelectedHeaders)]);

  useEffect(() => {
    setIsContentValidationStale(true);
  }, []);

  useEffect(() => {
    setWidthDetails(
      calculateTableColumnWidths(
        toCalculateColumnWidths,
        inputFileDetails.data,
      ),
    );
  }, [toCalculateColumnWidths, inputFileDetails.data]);
  const { columnWidths, tableWidthInPixels } = widthsDetails;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnType<any>[] = columnsRange.map((columnIndex) => {
    const validationResult = fileContentValidationResults[columnIndex];
    const validationResultClassName =
      (validationResult?.errorsCount || 0) === 0
        ? 'no-errors-column'
        : 'errors-column';

    const errorPercent =
      ((validationResult?.errorsCount || 0) * 100) / effectiveRowSamplingCount;
    return {
      title: () => {
        const node = validationResult ? (
          <div>{errorPercent?.toFixed(1)} % errors</div>
        ) : null;
        return fileContentValidationResults.length ? node : null;
      },
      className: validationResultClassName,
      children: [
        {
          title: inputFileDetails.headersInfo.hasHeaders ? (
            () => {
              return (
                <>{inputFileDetails.headersInfo.actualHeaders[columnIndex]}</>
              );
            }
          ) : (
            <></>
          ),
          children: [
            {
              width: columnWidths[columnIndex],
              minWidth: columnWidths[columnIndex],

              title: () => {
                const errorMessage = fieldErrors[columnIndex]?.message;
                const fieldHeader = fieldHeadersData[columnIndex];
                const classNames: string[] = [];
                if (
                  fieldHeader.experianHeader?.toLowerCase() !==
                  validatedHeaderMappings[
                    columnIndex
                  ].experianHeader?.toLowerCase()
                ) {
                  classNames.push('is-dirty');
                }
                if (errorMessage) {
                  classNames.push('is-errored');
                }

                const className = classNames.join(' ');
                /* console.log(
                  'headersSelectedStatusMap: ',
                  headersSelectedStatusMap,
                ); */
                return (
                  <div className={className}>
                    <FieldSelector
                      inputLabelsMap={inputLabelsMap}
                      errorMessage={errorMessage}
                      availableOptions={headersSelectedStatusMap.map(
                        (a) => a.header,
                      )}
                      selectedValue={fieldHeadersData[columnIndex]}
                      detectedHeaders={
                        inputFileDetails.headersInfo.detectedHeaders[
                          columnIndex
                        ]
                      }
                      onSelect={(value, _previous) => {
                        setIsValidating(true);
                        setTimeout(() => {
                          const {
                            fieldHeaderMappingsMap,
                            headerSelectionStatuses,
                          } = updateFieldMappings(
                            viewModel.fieldHeaderMappingsMap,
                            value,
                            inputLabelsMap,
                            inputFileDetails.headersInfo.hasHeaders || false,
                            columnIndex,
                          );
                          setViewModel((model) => {
                            const toReturn: InputFieldMapperViewModel = {
                              ...model,
                              fieldHeaderMappingsMap,
                              headersSelectedStatusMap: headerSelectionStatuses,
                            };
                            return toReturn;
                          });
                        });
                      }}
                    />
                  </div>
                );
              },
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              render: (text: any, row: any, index: any) => {
                const cellValue = inputFileDetails.data[columnIndex]?.[index];
                const fieldErrors =
                  fileContentValidationResults[columnIndex]?.errors[index];
                return (
                  <>
                    {fieldErrors && (
                      <ElementWithFeedbackContainer errors={fieldErrors}>
                        <Alert
                          isOpen
                          showIcon={false}
                          showCloseButton={false}
                          type='error'
                          description={cellValue}
                          backgroundColor='#e87a655e'
                        />
                      </ElementWithFeedbackContainer>
                    )}
                    {!fieldErrors && <>{cellValue}</>}
                  </>
                );
              },
            },
          ],
        },
      ],
    };
  });

  const validateData = (): void => {
    const result = validateFileContent(
      inputFileDetails.data,
      effectiveHeaders,
      inputLabelsMap,
    );
    setFileContentValidationResults(result);
    setIsContentValidationStale(false);
    setValidatedHeaderMappings(fieldHeadersData);
  };

  const getCurrentActiveFieldMappings = (): FieldMappings[] => {
    return inputFileDetails.headersInfo.actualHeaders.reduce(
      (acc, field, index) => {
        const value = !fieldHeadersData[index]?.experianHeader
          ? undefined
          : fieldHeadersData[index]?.experianHeader;
        const effectiveHeader = value || field;
        const recordIndex = acc.findIndex(
          (a) => a.field === (field || index.toString()),
        );
        const toInsert: FieldMappings = {
          field: field || index.toString(),
          headerNameWithLength: {
            name: value,
            fieldLength:
              inputLabelsMap[effectiveHeader.toLowerCase()]?.fieldLength || 50,
          },
        };
        if (recordIndex === -1) {
          acc.push(toInsert);
        } else {
          acc[recordIndex] = toInsert;
        }
        return acc;
      },
      [] as FieldMappings[],
    );
  };

  const uploadFileHandler = async (): Promise<void> => {
    if (inputFileDetails) {
      setIsLoading(true);
      const fmData = new FormData();
      const config = {
        headers: { 'content-type': 'multipart/form-data' },
      };
      fmData.append('tmsNumber', props.tmsNumber);
      fmData.append('subCode', props.subCode);
      fmData.append('file', inputFileDetails.inputFile || '');
      try {
        const res = await uploadInputFile(productName, fmData, config);
        setIsLoading(false);
        const { data, resultCode, messages } = res;
        if (data && resultCode === 0) {
          onSuccess(
            data?.originalFileName || '',
            data?.fileName || '',
            getCurrentActiveFieldMappings(),
            inputFileDetails.headersInfo.hasHeaders || false,
          );
        } else {
          const errorMessage =
            'Error while uploading file. ' + messages.join('\\n');
          handleFileUploadError(new Error(errorMessage));
        }
        // console.log('server res: ', res);
      } catch (err) {
        setIsLoading(false);
        handleFileUploadError(err);
      }
    }
  };

  const selectRemoteFileHandler = async (): Promise<void> => {
    if (inputFileDetails) {
      setIsLoading(true);
      if (isLayoutRequired.saveLayout) {
        await saveLayoutHandler();
      }
      setTimeout(() => {
        onSuccess(
          inputFileDetails?.inputFile?.name ||
            inputFileDetails?.clientFileName ||
            '',
          inputFileDetails?.experianFileName || '',
          getCurrentActiveFieldMappings(),
          inputFileDetails.headersInfo.hasHeaders || false,
          inputFileDetails?.recordsExpected || undefined,
        );
        setIsLoading(false);
      }, 1000);
    }
  };

  const saveLayoutHandler = async (): Promise<void> => {
    if (inputFileDetails) {
      const LayooutDetails = inputFileDetails.headersInfo.actualHeaders.reduce(
        (acc, field, index) => {
          const value = !fieldHeadersData[index]?.experianHeader
            ? undefined
            : fieldHeadersData[index]?.experianHeader;
          const recordIndex = acc.findIndex(
            (a) => a.clientHeader === (field || index.toString()),
          );
          const toInsert: FileLayout = {
            experianHeader: value || null,
            clientHeader: field,
          };
          if (recordIndex === -1) {
            acc.push(toInsert);
          } else {
            acc[recordIndex] = toInsert;
          }
          return acc;
        },
        [] as FileLayout[],
      );
      await saveLayout(inputFileDetails.fileId as string, LayooutDetails)
        .then((response) => {
          if (response.status === 'Success')
            modalUtils.showSuccessMessage('Layout saved Successfully.');
          else
            modalUtils.showError(
              response.message,
              'Error while saving Layout.',
            );
        })
        .catch((error) => {
          modalUtils.showError(error, 'Error while saving Layout.');
        });
    }
  };

  // console.log('fieldErrors: ', fieldErrors.constructor.name, fieldErrors);
  const hasAnyFieldErrors =
    fieldErrors.constructor === Array
      ? fieldErrors.some((a) => a.message) ||
        aggregateErrors.some((a) => a.length > 0)
      : false;
  const hasAnyContentErrors = fileContentValidationResults.some(
    (a) => a?.errorsCount > 0,
  );
  const wasValidatedOnce = fileContentValidationResults.length > 0;
  // const hasAnyFieldHeaderErrors =
  //   (viewModel.fieldErrors || []).length > 0 ||
  //   (viewModel.aggregateErrors || []).length > 0;
  // const canShowUpload = !hasAnyFieldHeaderErrors && isContentValidated;
  return (
    <>
      {/* <pre>
        {JSON.stringify(
          {
            // isContentValidationStale,
            // hasAnyFieldErrors,
            // hasAnyContentErrors,
            // fieldErrors,
            // validatedHeaderMappings,
            // effectiveHeaders,
            // fieldHeadersData,
            // headersSelectedStatusMap,
            isLoading,
            isValidating,
          },
          null,
          2,
        )}
      </pre> */}
      <Spin spinning={isLoading || isValidating} size='lg' delay={0}>
        <form>
          <Table
            onHeaderRow={(cols, index) => {
              return {
                className:
                  index === 0
                    ? hasAnyContentErrors
                      ? 'errors-row'
                      : wasValidatedOnce
                      ? 'no-errors-row'
                      : 'hide'
                    : '',
              };
            }}
            size='small'
            className='input-file-fields-and-content-table'
            dataSource={rows}
            columns={columns}
            pagination={false}
            bordered
            scroll={{ x: tableWidthInPixels, y: tableHeightInPixels }}
            rowKey={() => generateId()}
          ></Table>
          <HStack justify='end' mt={2.5} align='end'>
            <InputFileSelector
              onFileClicked={() => {
                setIsLoading(true);
                console.log('File Clicked');
              }}
              onCancel={() => {
                setIsLoading(false);
              }}
              subCode={props.subCode}
              isCompact={true}
              onFileSelected={(details) => {
                props.onNewFileSelected(
                  details,
                  getCurrentActiveFieldMappings(),
                );
              }}
              setIsRemoteFile={props.setIsRemoteFile}
              inputFileSelectionOptions={inputFileSelectionOptions}
              productName={productName}
              onSuccess={(uploadResult) => {
                props.onSuccess(
                  uploadResult.clientFileName,
                  uploadResult.experianFileName,
                  uploadResult.fieldMappings,
                  uploadResult.hasHeaders,
                  uploadResult.recordsExpected,
                );
              }}
              inputLabelsMap={inputLabelsMap}
              oneClickRemoteFileAccess={props.oneClickRemoteFileAccess}
              isModalVisible={isModalVisible}
              setIsModalVisible={setIsModalVisible}
            />
            <Button
              colorScheme='blue'
              onClick={() => validateData()}
              disabled={hasAnyFieldErrors}
            >
              <FaCheckDouble />
              <Text ml={1}>Validate 100 rows</Text>
            </Button>

            {!hasAnyContentErrors && props.isRemoteFile && (
              <Tooltip
                label={
                  isContentValidationStale
                    ? 'Please validate data to proceed.'
                    : ''
                }
              >
                <Button
                  colorScheme='blue'
                  disabled={isContentValidationStale || hasAnyFieldErrors}
                  onClick={selectRemoteFileHandler}
                  color={'white'}
                >
                  <FaCheck color='white' />
                  <Text color={'white'} ml={1}>
                    Accept
                  </Text>
                </Button>
              </Tooltip>
            )}

            {hasAnyContentErrors && props.isRemoteFile && (
              <Popover
                placement='top'
                title={'The file has errors. Do you want to proceed?'}
                onOk={selectRemoteFileHandler}
                okProps={{ bg: 'blue.700' }}
                okText='Yes'
                cancelText='No'
                triggerButtonProps={{
                  color: 'red.500',
                  border: '1px solid',
                  disabled: isContentValidationStale || hasAnyFieldErrors,
                }}
              >
                <FaCheck />
                <Text ml={1}>Accept</Text>
              </Popover>
            )}

            {!hasAnyContentErrors && !props.isRemoteFile && (
              <Tooltip
                title={
                  isContentValidationStale
                    ? 'Please validate data to proceed with upload.'
                    : ''
                }
              >
                <Button
                  colorScheme='blue'
                  disabled={isContentValidationStale || hasAnyFieldErrors}
                  onClick={uploadFileHandler}
                >
                  <Icon as={AiOutlineUpload} />
                  <Text ml={1}>Upload</Text>
                </Button>
              </Tooltip>
            )}

            {hasAnyContentErrors && !props.isRemoteFile && (
              <Popover
                placement='top'
                title={'The file has errors. Do you want to upload?'}
                onOk={uploadFileHandler}
                okProps={{ bg: 'blue.700' }}
                okText='Yes'
                cancelText='No'
                triggerButtonProps={{
                  color: 'red.500',
                  border: '1px solid',
                  disabled: isContentValidationStale || hasAnyFieldErrors,
                }}
              >
                <Icon as={AiOutlineUpload} />
                <Text ml={1}>Upload</Text>
              </Popover>
            )}
          </HStack>
        </form>
      </Spin>
    </>
  );
};
